
export type Store = {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  status?: string;
  // Backward compatibility
  categoryId: string;
  category: {
    title: {
      ar: string;
      en: string;
    };
    slug: string;
  };
  // New multi-category structure
  categories?: Array<{
    categoryId: string;
    category: {
      title: {
        ar: string;
        en: string;
      };
      slug: string;
    };
    relevanceScore: number;
  }>;
  prominentCategory?: {
    categoryId: string;
    category: {
      title: {
        ar: string;
        en: string;
      };
      slug: string;
    };
    relevanceScore: number;
  };
  tags?: string[];
  location?: string;
  platforms: {
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    whatsapp?: string;
    website?: string;
    phone?: string;
  };
  rating: number;
  totalReviews: number;
  logoUrl?: string;
  featured: boolean;
  verified: boolean;
  // D-09: true when this store has an approved StoreClaim (public verified-owner signal).
  hasVerifiedOwner?: boolean;
  yetfarragRating?: number; // Internal rating (0-10) for sorting, hidden from users, visible to admins
  googlePlaces?: {
    placeId: string;
    name: string;
    address: string;
    rating: number;
    reviewCount: number;
    photoUrl?: string;
    phoneNumber?: string;
    website?: string;
    priceLevel?: number;
    openingHours?: string[];
    geometry?: {
      lat: number;
      lng: number;
    };
    types?: string[];
  };
  // Phase 32 (D-04, amended post-UAT): mirrors the PUBLIC `StoreBranch` shape in
  // shared/src/store.ts. The frontend does not depend on `shared`, so the public fields are
  // inlined here and MUST be kept in step with it — a field missing here is silently dropped if
  // this payload is ever narrowed to the declared type. googleRating/googleReviewCount are public
  // since the amendment and are rendered by StoreBranchesCard. Still absent, deliberately: the two
  // sync timestamps (admin-only) and photoUrl (its URL embeds the server API key, T-32-12).
  branches?: Array<{
    name: string;
    googlePlaceId?: string;
    isPrimary?: boolean;
    address?: string;
    governorate?: string;
    phone?: string;
    workingHours?: string[];
    geometry?: { lat: number; lng: number };
    mapsUrl?: string;
    googleRating?: number;
    googleReviewCount?: number;
  }>;
  reviewSummary?: {
    text: string;
    pros: string[];
    cons: string[];
    sentiment: "positive" | "mixed" | "negative";
    reviewCount: number;
    sources: {
      yetfarrag: number;
      facebook_group: number;
      google_maps: number;
    };
    generatedAt: string;
  };
  nameEn?: string;
  shortDescription?: string;
  descriptionEn?: string;
  keywords?: string[];
  topProducts?: Array<{
    name: string;
    nameEn?: string;
    price?: string;
    imageUrl?: string;
    url?: string;
  }>;
  metadata?: Record<string, unknown>;
  // quick-260813-ttw: merchant-owned top-level fields — QuickDetails prefers these over scraped
  // metadata when set, so a merchant edit actually renders on the public page.
  workingHours?: string;
  paymentOptions?: string[];
  refundPolicy?: string;
  deliveryGovernorates?: string[];
  // Approval-gated server-side: this array only ever contains published values.
  customFields?: { key: string; label: string; value: string }[];
  createdAt: string;
  updatedAt: string;
};

export type Category = {
  value: string;
  label: string;
  icon: string;
};

export type StoresResponse = {
  success: boolean;
  data: Store[];
  pagination: {
    current_page: number;
    items_per_page: number;
    has_next_page: boolean;
    has_prev_page: boolean;
  };
};

export type StoreResponse = {
  success: boolean;
  data: Store;
};

const API_URL = typeof window === 'undefined'
  ? (process.env.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8080/api')
  : import.meta.env.VITE_API_URL;

export const fetchStores = async (params: {
  page?: number;
  category?: string;
  search?: string;
  sort?: string;
  featured?: boolean;
  verified?: boolean;
  limit?: number;
  tags?: string;
  hasFacebook?: boolean;
  hasInstagram?: boolean;
  hasWebsite?: boolean;
  minRating?: number;
}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  else queryParams.append("limit", "12");

  if (params.category) queryParams.append("category", params.category);
  if (params.search) queryParams.append("search", params.search);
  if (params.sort) queryParams.append("sort", params.sort);
  if (params.featured) queryParams.append("featured", "true");
  if (params.verified) queryParams.append("verified", "true");
  if (params.tags) queryParams.append("tags", params.tags);
  if (params.hasFacebook) queryParams.append("hasFacebook", "true");
  if (params.hasInstagram) queryParams.append("hasInstagram", "true");
  if (params.hasWebsite) queryParams.append("hasWebsite", "true");
  if (params.minRating) queryParams.append("minRating", String(params.minRating));

  const response = await fetch(`${API_URL}/stores?${queryParams}`);

  if (!response.ok) {
    throw new Error("فشل في تحميل المتاجر");
  }

  return response.json() as Promise<StoresResponse>;
};

export const fetchStore = async ({ id }: { id: string }) => {
  const response = await fetch(`${API_URL}/stores/${id}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("المتجر غير موجود");
    }
    throw new Error("فشل في تحميل بيانات المتجر");
  }

  return response.json() as Promise<StoreResponse>;
};

// quick-260815-dhs: published stores sharing this store's category (mobile rail).
// Backend responds { ok: true, data: Store[] } — unwrap to the array here.
export const getSimilarStores = async ({ id }: { id: string }): Promise<Store[]> => {
  const response = await fetch(`${API_URL}/stores/${id}/similar`);

  if (!response.ok) {
    throw new Error("فشل في تحميل المتاجر المشابهة");
  }

  const result = await response.json();
  return (result.data ?? []) as Store[];
};

// Backend /stores/categories/list returns { id, title: { ar, en }, slug, description }.
// The browse-mode <Select> renders { value, label } — map the API shape to it here
// (value = slug because the browse endpoint's category filter accepts slug or id).
type CategoryListItem = {
  id: string;
  title: { ar: string; en: string };
  slug: string;
  description?: string;
};

export const fetchCategories = async () => {
  const response = await fetch(`${API_URL}/stores/categories/list`);

  if (!response.ok) {
    throw new Error("فشل في تحميل الفئات");
  }

  const result = await response.json();
  return (result.data as CategoryListItem[]).map((cat) => ({
    value: cat.slug,
    label: cat.title?.ar || cat.title?.en || cat.slug,
    icon: "",
  })) as Category[];
};

export type PopularTag = {
  tag: string;
  count: number;
};

export const fetchPopularTags = async (): Promise<PopularTag[]> => {
  const response = await fetch(`${API_URL}/stores/tags/popular`);

  if (!response.ok) {
    throw new Error("فشل في تحميل العلامات");
  }

  const result = await response.json();
  return result.data as PopularTag[];
};
